/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import * as React from 'react'
import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import HDSCard from '@hashicorp/react-hds/src/components/card'
import { TextPlus } from '@hashicorp/react-hds/src/components/text'
import { IconArrowRight24 } from '@hashicorp/flight-icons/svg-react/arrow-right-24'
import ProductBadge from '../product-badge'
import type {
	CardPrimitiveProps,
	ThumbnailProps,
	MetaProps,
	ContentProps,
	HeadingProps,
	ProductBadgesProps,
	DescriptionProps,
} from './types'
import s from './style.module.css'

const Card = React.forwardRef<HTMLAnchorElement, CardPrimitiveProps>(
	(props, ref) => {
		const {
			withArrow = true,
			heading,
			link,
			clickHandler,
			isExternal,
			hasBorder = true,
			children,
			className,
			'aria-label': ariaLabel,
		} = props

		const linkProps = isExternal
			? { target: '_blank', rel: 'noopener noreferrer' }
			: {}

		return (
			<HDSCard
				level="mid"
				levelHover="high"
				className={classNames(s.card, className)}
				overflow="hidden"
				hasBorder={hasBorder}
			>
				<div className={s.contentContainer}>
					{children}

					<div className={s.cta} data-testid="wpl-card-cta">
						{withArrow ? <IconArrowRight24 /> : null}
					</div>

					<Link
						href={link}
						ref={ref}
						className={s.link}
						aria-label={ariaLabel ?? heading}
						onClickCapture={clickHandler}
						{...linkProps}
					/>
				</div>
			</HDSCard>
		)
	}
)

function Thumbnail({ src, alt }: ThumbnailProps) {
	return (
		<div className={s.thumbnail} data-testid="wpl-card-thumbnail">
			<div className={s.image}>
				<Image src={src} alt={alt} width={800} height={450} />
			</div>
		</div>
	)
}

function PersonThumbnail({ src, alt }: ThumbnailProps) {
	return (
		<div className={s.personThumbnail} data-testid="wpl-card-thumbnail">
			<div className={s.image}>
				<Image src={src} alt={alt} width={800} height={450} />
			</div>
		</div>
	)
}

function LogoThumbnail({ src, alt }: ThumbnailProps) {
	return (
		<div className={s.logoThumbnail} data-testid="wpl-card-logo-thumbnail">
			<div className={s.image}>
				<Image src={src} alt={alt} width={800} height={450} />
			</div>
		</div>
	)
}

function Meta({ items }: MetaProps) {
	return (
		<div className={s.meta} data-testid="wpl-card-meta">
			<TextPlus.Label>
				{items.map((item, stableIdx) => {
					const isLastItem = stableIdx === items.length - 1
					return (
						<>
							{item}
							{!isLastItem ? (
								<span className={s.metaSeparator} aria-hidden={true}>
									|
								</span>
							) : null}
						</>
					)
				})}
			</TextPlus.Label>
		</div>
	)
}

function Content({ children }: ContentProps) {
	return (
		<div className={s.content} data-testid="wpl-card-content">
			{children}
		</div>
	)
}

function Heading({ as = 'h2', children }: HeadingProps) {
	return (
		<TextPlus.DisplayExpressive tag={as} size="200" className={s.heading}>
			{children}
		</TextPlus.DisplayExpressive>
	)
}

function ProductBadges({ badges, children }: ProductBadgesProps) {
	return (
		<div className={s.productBadges} data-testid="wpl-card-badges">
			{badges.map((badge, stableIdx) => {
				return (
					<ProductBadge
						// eslint-disable-next-line react/no-array-index-key
						key={`product-badge-${stableIdx}`}
						productName={badge}
						hasDot={true}
					/>
				)
			})}
			{children}
		</div>
	)
}

function Description({ children }: DescriptionProps) {
	return (
		<TextPlus.Body tag="p" size="200" className={s.description}>
			{children}
		</TextPlus.Body>
	)
}

Card.displayName = 'Card'
export {
	Card,
	Thumbnail,
	PersonThumbnail,
	LogoThumbnail,
	Meta,
	Content,
	Heading,
	Description,
	ProductBadges,
}
